// JoinRoom page - public landing page for participants
// Implements US-1.2: Join Room as Participant

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { joinRoom } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import insurtaleLogo from '../assets/insurtale-logo.webp';
import styles from './JoinRoom.module.css';

const JoinRoom: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();

  // HANDLE URL parameters for pre-filled code
  useEffect(() => {
    const urlCode = searchParams.get('code');

    // PRE-FILL room code from URL
    if (urlCode) {
      setCode(urlCode);
    }
  }, [searchParams]);

  // FORMAT room code as XXXX-XXXX while typing
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits

    // LIMIT to 8 digits
    if (value.length > 8) {
      value = value.slice(0, 8);
    }

    // FORMAT with hyphen after 4 digits
    if (value.length > 4) {
      value = `${value.slice(0, 4)}-${value.slice(4)}`;
    }

    setCode(value);
  };

  // SUBMIT room join request
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // VALIDATE room code length
      const codeWithoutHyphen = code.replace(/-/g, '');
      if (codeWithoutHyphen.length !== 8) {
        setError('Raum-Code muss 8-stellig sein');
        setLoading(false);
        return;
      }

      // ATTEMPT to join room
      const response = await joinRoom(code, displayName || undefined);

      // STORE session data in localStorage
      localStorage.setItem('elegy_session_id', response.session_token);
      localStorage.setItem('elegy_room_id', response.room_id);

      // REDIRECT based on room phase
      if (response.room_state.phase === 'collecting') {
        navigate(`/room/${response.room_id}`);
      } else if (response.room_state.phase === 'facilitating') {
        navigate(`/room/${response.room_id}`);
      } else {
        setError('Diese Retro wurde beendet');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to join room';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // SHOW loading state while checking authentication
  if (authLoading) {
    return (
      <div className={styles.joinRoomContainer}>
        <div className={styles.joinRoomCard}>
          <div className={styles.joinRoomHeader}>
            <img src={insurtaleLogo} alt="insur_tale" className={styles.logo} />
            <p>Authentifizierung wird überprüft...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.joinRoomContainer}>
      <div className={styles.joinRoomCard}>
        <div className={styles.joinRoomHeader}>
          <img src={insurtaleLogo} alt="insur_tale" className={styles.logo} />
          <h1>Elegy</h1>
          {isAuthenticated && user ? (
            <p>Willkommen zurück, {user.name?.split(' ')[0] || user.username.charAt(0).toUpperCase() + user.username.slice(1)}!</p>
          ) : (
            <p>An Retro teilnehmen</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className={styles.joinRoomForm}>
          <div className={styles.formGroup}>
            <label htmlFor="code">Raum-Code</label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="XXXX-XXXX"
              maxLength={9} // 8 digits + 1 hyphen
              autoComplete="off"
              autoFocus
              required
              className={styles.roomCodeInput}
            />
            <span className={styles.inputHint}>Gib den 8-stelligen Raum-Code ein</span>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="displayName">Dein Name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Max Mustermann"
              maxLength={100}
              required
              className={styles.displayNameInput}
            />
            <span className={styles.inputHint}>Name wird nur sichtbar wenn Anonymer Modus ausgeschaltet</span>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button type="submit" disabled={loading} className={styles.joinButton}>
            {loading ? 'Beitreten...' : 'Raum beitreten'}
          </button>
        </form>

        {isAuthenticated && (
          <div className={styles.joinRoomFooter}>
            <div className={styles.divider}>oder</div>
            <button
              className={styles.createButton}
              onClick={() => navigate('/create')}
              type="button"
            >
              Neue Retro erstellen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinRoom;
